import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react'
import { toast } from 'sonner'
import { api, ApiError, type SignInPayload } from '@/lib/api'

const SESSION_KEY = 'unitexrpl_session'
const PENDING_PAYLOAD_KEY = 'unitexrpl_pending_payload'

export interface WalletAccount {
  address: string
}

interface WalletContextValue {
  account: WalletAccount | null
  connected: boolean
  connecting: boolean
  signInPayload: SignInPayload | null
  startSignIn: () => Promise<void>
  handleSignInSuccess: (sessionId: string, walletAddress: string) => void
  cancelSignIn: () => void
  disconnect: () => Promise<void>
}

const WalletContext = createContext<WalletContextValue | null>(null)

export function useWallet(): WalletContextValue {
  const ctx = useContext(WalletContext)
  if (!ctx) throw new Error('useWallet must be used within <WalletProvider>')
  return ctx
}

export function WalletProvider({ children }: { children: ReactNode }) {
  const [account, setAccount] = useState<WalletAccount | null>(null)
  const [connecting, setConnecting] = useState(false)
  const [signInPayload, setSignInPayload] = useState<SignInPayload | null>(null)

  const startSignIn = useCallback(async () => {
    if (connecting) return
    setConnecting(true)

    try {
      const returnUrl = window.location.origin + window.location.pathname
      const { data } = await api.createSignIn(returnUrl)

      // Persist payload UUID so we can recover after mobile redirect
      sessionStorage.setItem(PENDING_PAYLOAD_KEY, JSON.stringify(data))
      setSignInPayload(data)
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : 'Failed to start sign-in'
      toast.error(message)
      setConnecting(false)
    }
  }, [connecting])

  const handleSignInSuccess = useCallback((sessionId: string, walletAddress: string) => {
    localStorage.setItem(SESSION_KEY, sessionId)
    sessionStorage.removeItem(PENDING_PAYLOAD_KEY)
    setAccount({ address: walletAddress })
    setSignInPayload(null)
    setConnecting(false)
    toast.success('Wallet connected')
  }, [])

  const cancelSignIn = useCallback(() => {
    sessionStorage.removeItem(PENDING_PAYLOAD_KEY)
    setSignInPayload(null)
    setConnecting(false)
  }, [])

  const disconnect = useCallback(async () => {
    try {
      await api.logout()
    } catch {
      // best-effort
    }
    localStorage.removeItem(SESSION_KEY)
    setAccount(null)
    toast.info('Wallet disconnected')
  }, [])

  // On mount: restore existing session OR check for pending mobile sign-in
  useEffect(() => {
    const init = async () => {
      // 1. Try to restore an existing session
      const sessionId = localStorage.getItem(SESSION_KEY)
      if (sessionId) {
        try {
          const { data } = await api.getSession()
          setAccount({ address: data.session.walletAddress })
          return
        } catch {
          localStorage.removeItem(SESSION_KEY)
        }
      }

      // 2. Check for a pending payload (mobile return scenario)
      const pendingRaw = sessionStorage.getItem(PENDING_PAYLOAD_KEY)
      if (pendingRaw) {
        try {
          const pending: SignInPayload = JSON.parse(pendingRaw)

          // Check if the user already signed while we were away
          const { data } = await api.checkSignIn(pending.uuid)
          if (data.status === 'signed') {
            localStorage.setItem(SESSION_KEY, data.sessionId)
            sessionStorage.removeItem(PENDING_PAYLOAD_KEY)
            setAccount({ address: data.walletAddress })
            toast.success('Wallet connected')
            return
          }

          if (data.status === 'expired' || data.status === 'rejected') {
            sessionStorage.removeItem(PENDING_PAYLOAD_KEY)
            return
          }

          // Still pending — re-show the dialog so WebSocket can pick up
          setSignInPayload(pending)
          setConnecting(true)
        } catch {
          sessionStorage.removeItem(PENDING_PAYLOAD_KEY)
        }
      }
    }
    init()
  }, [])

  const value = useMemo<WalletContextValue>(
    () => ({
      account,
      connected: !!account,
      connecting,
      signInPayload,
      startSignIn,
      handleSignInSuccess,
      cancelSignIn,
      disconnect,
    }),
    [account, connecting, signInPayload, startSignIn, handleSignInSuccess, cancelSignIn, disconnect],
  )

  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>
}
